import PropTypes from "prop-types";
import React, { useContext, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

/**
 * TrackingConfig
 *
 * Responsibility:
 * - Render the dynamic table for Tracking Shipments (external integration data).
 * - Allow only Create and View operations.
 * - Redirect CRUD actions to the TrackingConfigCrud route (component created later).
 */
export default function TrackingConfig({ roles, onCreateTab, toastMessages, refresh, setRefresh }) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    /**
     * Removes context menu actions that must not be available on this screen.
     * The table must expose only Create and View.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy", "Update", "Delete"],
        []
    );

    /**
     * Centralizes the CRUD route to avoid hard-coded duplicates.
     * This route is opened as a dynamic tab via onCreateTab().
     */
    const CRUD_ROUTE = "/home/tracking/trackingConfigCrud";

    //====================================================================================================
    // openTrackingShipmentCrud:
    // - Persists the operation in global state so the CRUD screen can infer the desired mode.
    // - For View, persists the selected row data.
    // - Validates row presence when required and shows a localized toast if missing.
    // - Builds a consistent tab label using i18n.
    //====================================================================================================
    const openTrackingShipmentCrud = useCallback(
        (operation, row) => {
            dispatch(M_StateActions.addM_State({ key: "operation", value: operation }));

            if (operation === "View") {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(M_StateActions.addM_State({ key: "TrackingShipmentLine", value: row }));
            }

            const tabLabel =
                operation === "Create"
                    ? t("MenuTrackingLabel")
                    : `${t("MenuTrackingLabel")} - ${t("View")}`;

            onCreateTab(CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    //====================================================================================================
    // replaceFunctions:
    // - Memoized map of context actions consumed by MTable.
    // - Only Create and View are exposed.
    //====================================================================================================
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openTrackingShipmentCrud("View", row)],
                ["Create", () => openTrackingShipmentCrud("Create")],
            ]),
        [openTrackingShipmentCrud]
    );

    return (
        <div className="divGlobalSettings">
            <div className="subTitleSettings">
                <div>
                    <div className="titleSettings" style={{ color: ctx.secondaryColor }}>
                        {t("MenuTrackingLabel")}
                    </div>
                    <div className="labelSubtitleSettings">{t("InfoTracking")}</div>
                </div>
            </div>

            <MTable
                filtersOnTable
                entityName={"TrackingShipments"}
                roleName={"Tracking"}
                rolePermissions={roles?.map((vl) => vl.Value)}
                removeContextItems={removeContextItems}
                refresh={refresh}
                setRefresh={setRefresh}
                useSessionStorage={false}
                replaceFunctions={replaceFunctions}
            />
        </div>
    );
}

TrackingConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
    refresh: PropTypes.bool,
    setRefresh: PropTypes.func,
};